/**
 * Logging configuration and presets
 * Manages logging configuration based on environment settings
 */

import type { LogConfig, LogPreset } from './types';

/**
 * Logging presets for different scenarios
 */
export const LOG_PRESETS: Record<LogPreset, LogConfig> = {
  // Minimal logging for production
  production: {
    enabledLevels: { L1: true, L2: true, L3: false, L4: false, L5: false },
    enabledDomains: {
      compliance: false,
      optimization: false,
      distribution: false,
      interest: false,
      validation: false,
      performance: true,
    },
    showTimestamp: true,
    showContext: false,
    showLocation: false,
    debugSessions: false,
  },

  // Balanced logging for development
  development: {
    enabledLevels: { L1: true, L2: true, L3: true, L4: true, L5: false },
    enabledDomains: {
      compliance: true,
      optimization: true,
      distribution: true,
      interest: true,
      validation: true,
      performance: true,
    },
    showTimestamp: true,
    showContext: true,
    showLocation: false,
    debugSessions: true,
  },

  // Full logging for debugging
  debugging: {
    enabledLevels: { L1: true, L2: true, L3: true, L4: true, L5: true },
    enabledDomains: {
      compliance: true,
      optimization: true,
      distribution: true,
      interest: true,
      validation: true,
      performance: true,
    },
    showTimestamp: true,
    showContext: true,
    showLocation: true,
    debugSessions: true,
  },

  // No logging except errors
  silent: {
    enabledLevels: { L1: false, L2: false, L3: false, L4: false, L5: false },
    enabledDomains: {
      compliance: false,
      optimization: false,
      distribution: false,
      interest: false,
      validation: false,
      performance: false,
    },
    showTimestamp: false,
    showContext: false,
    showLocation: false,
    debugSessions: false,
  },

  // Everything enabled (use sparingly)
  verbose: {
    enabledLevels: { L1: true, L2: true, L3: true, L4: true, L5: true },
    enabledDomains: {
      compliance: true,
      optimization: true,
      distribution: true,
      interest: true,
      validation: true,
      performance: true,
    },
    showTimestamp: true,
    showContext: true,
    showLocation: true,
    debugSessions: true,
  },

  // Domain-specific debugging presets
  'debug-distribution': {
    enabledLevels: { L1: false, L2: false, L3: true, L4: true, L5: false },
    enabledDomains: {
      compliance: false,
      optimization: false,
      distribution: true,
      interest: false,
      validation: false,
      performance: false,
    },
    showTimestamp: true,
    showContext: true,
    showLocation: true,
    debugSessions: false,
  },

  'debug-interest': {
    enabledLevels: { L1: false, L2: false, L3: true, L4: true, L5: true },
    enabledDomains: {
      compliance: false,
      optimization: false,
      distribution: false,
      interest: true,
      validation: false,
      performance: false,
    },
    showTimestamp: true,
    showContext: true,
    showLocation: true,
    debugSessions: false,
  },

  'debug-compliance': {
    enabledLevels: { L1: false, L2: true, L3: true, L4: true, L5: false },
    enabledDomains: {
      compliance: true,
      optimization: false,
      distribution: false,
      interest: false,
      validation: true,
      performance: false,
    },
    showTimestamp: true,
    showContext: true,
    showLocation: true,
    debugSessions: false,
  },

  'debug-optimization': {
    enabledLevels: { L1: false, L2: true, L3: true, L4: true, L5: false },
    enabledDomains: {
      compliance: true,
      optimization: true,
      distribution: false,
      interest: false,
      validation: false,
      performance: true,
    },
    showTimestamp: true,
    showContext: true,
    showLocation: true,
    debugSessions: false,
  },

  'debug-engines': {
    enabledLevels: { L1: false, L2: false, L3: false, L4: true, L5: false },
    enabledDomains: {
      compliance: true,
      optimization: true,
      distribution: true,
      interest: true,
      validation: true,
      performance: false,
    },
    showTimestamp: true,
    showContext: true,
    showLocation: true,
    debugSessions: false,
  },

  'debug-calculators': {
    enabledLevels: { L1: false, L2: false, L3: false, L4: false, L5: true },
    enabledDomains: {
      compliance: false,
      optimization: false,
      distribution: false,
      interest: false,
      validation: false,
      performance: false,
    },
    showTimestamp: true,
    showContext: true,
    showLocation: true,
    debugSessions: false,
  },

  'performance-analysis': {
    enabledLevels: { L1: true, L2: true, L3: false, L4: false, L5: false },
    enabledDomains: {
      compliance: false,
      optimization: false,
      distribution: false,
      interest: false,
      validation: false,
      performance: true,
    },
    showTimestamp: true,
    showContext: true,
    showLocation: false,
    debugSessions: false,
  },
};

/**
 * Get the current logging configuration from environment
 */
export function getLogConfig(): LogConfig {
  // Check for environment variable
  const presetName = (import.meta.env.VITE_BOND_LOG_PRESET as LogPreset) || 'development';

  // Return the preset configuration
  const preset = LOG_PRESETS[presetName];
  if (!preset) {
    console.warn(`Unknown log preset: ${presetName}, falling back to 'development'`);
    return LOG_PRESETS.development;
  }

  return preset;
}

/**
 * Check if a specific level is enabled
 */
export function isLevelEnabled(
  level: keyof LogConfig['enabledLevels'],
  config?: LogConfig
): boolean {
  const currentConfig = config || getLogConfig();
  return currentConfig.enabledLevels[level] || false;
}

/**
 * Check if a specific domain is enabled
 */
export function isDomainEnabled(
  domain: keyof LogConfig['enabledDomains'],
  config?: LogConfig
): boolean {
  const currentConfig = config || getLogConfig();
  return currentConfig.enabledDomains[domain] || false;
}

/**
 * Get a string representation of the current configuration
 */
export function getConfigSummary(): string {
  const config = getLogConfig();
  const enabledLevels = Object.entries(config.enabledLevels)
    .filter(([_, enabled]) => enabled)
    .map(([level]) => level);
  const enabledDomains = Object.entries(config.enabledDomains)
    .filter(([_, enabled]) => enabled)
    .map(([domain]) => domain);

  return `Levels: ${enabledLevels.join(', ') || 'none'} | Domains: ${enabledDomains.join(', ') || 'none'}`;
}
